import type { BetId, IncursionRisk, SideId } from '@somnhold/content';
import { startPlayBeat } from './play-beat.ts';
import { campaignDay, momentAt, previewWindow, waveAt, type CalendarSpec, type ChatLine, type CommitReply, type Moment, type SessionOpen } from '@somnhold/protocol';
import {
  advanceTime,
  calendarMarea,
  applyCampaignCommand,
  createBattle,
  type BattleParams,
  type BattleState,
  type CampaignCommand,
  type CampaignState,
  type Command,
  type CommandResult,
} from '@somnhold/sim';
import type { CampaignService, PlayedBattle, Presence } from './service.ts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

export const api = async <T>(path: string, init: { method?: string; body?: unknown; timeoutMs?: number } = {}): Promise<T> => {
  const res = await fetch(path, {
    signal: AbortSignal.timeout(init.timeoutMs ?? 20_000),
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers: init.body !== undefined ? { 'content-type': 'application/json' } : {},
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (!res.ok) {
    let code = 'http';
    try {
      code = ((await res.json()) as { error?: string }).error ?? code;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, code);
  }
  return (await res.json()) as T;
};

interface StateView {
  state: CampaignState;
  stateVersion: number;
  stateHash: string;
  name: string;
  calendar: CalendarSpec;
  /** Who already played each sector of the next wave ("marea:wave:sector" → player). */
  playedBy?: Record<string, string>;
}

interface Entry {
  seq: number;
  at: number;
  cmd: CampaignCommand;
}

interface IssuedBattle {
  id: string;
  marea: number;
  wave: number;
  sector: number;
  params: BattleParams;
  live: boolean;
}

/**
 * A campaign that lives in its Durable Object. Commands apply locally at once (optimistic) and go
 * up as a session log; the server re-simulates them and answers accepted / rebased / rejected.
 */
export class ServerCampaign implements CampaignService {
  readonly kind = 'server' as const;
  state!: CampaignState;
  name = '';
  private cal!: CalendarSpec;
  private sid = '';
  private openedAt = 0;
  private offset = 0;
  private seq = 0;
  private outbox: Entry[] = [];
  private battles: PlayedBattle[] = [];
  private played = new Set<string>();
  private incursionsOut: { id: string; log: { tick: number; cmds: Command[] }[] }[] = [];
  private sagasOut: { id: string; log: { tick: number; cmds: Command[] }[] }[] = [];
  private flushTimer = 0;
  private pollTimer = 0;
  private listeners = new Set<() => void>();
  private inflight: Promise<void> | null = null;
  offline = false;
  /** Called with the number of discarded commands after a rebase. */
  onRebase: (discarded: number, why?: string[]) => void = () => undefined;
  onNetwork: (offline: boolean) => void = () => undefined;

  private constructor(
    readonly id: string,
    readonly me: string,
  ) {}

  static async open(id: string, me: string): Promise<ServerCampaign> {
    const c = new ServerCampaign(id, me);
    const view = await api<StateView>(`/api/campaigns/${id}/state`);
    c.cal = view.calendar;
    c.name = view.name;
    c.notePlayed(view.playedBy);
    await c.openSession();
    window.addEventListener('pagehide', c.onHide);
    c.stopBeat = startPlayBeat(id);
    return c;
  }

  private onHide = (): void => {
    // Best effort: send what's pending as the page goes away.
    if (this.outbox.length === 0) return;
    const body = JSON.stringify({ entries: this.outbox, battles: [], final: true });
    navigator.sendBeacon?.(`/api/campaigns/${this.id}/sessions/${this.sid}/commit`, new Blob([body], { type: 'application/json' }));
  };

  private async openSession(): Promise<void> {
    const t0 = Date.now();
    const s = await api<SessionOpen>(`/api/campaigns/${this.id}/sessions`, { body: {} });
    const t1 = Date.now();
    this.sid = s.sessionId;
    this.openedAt = s.openedAt;
    this.offset = s.openedAt - Math.round((t0 + t1) / 2);
    this.seq = 0;
    this.workMinutes = 0;
    this.outbox = [];
    this.state = s.state as CampaignState;
    this.emit();
  }

  calendar(): CalendarSpec {
    return this.cal;
  }

  now(): number {
    return Date.now() + this.offset;
  }

  moment(): Moment {
    return momentAt(this.cal, this.state.mareaIndex, this.state.waveIndex, this.now(), this.state.status === 'active', calendarMarea(this.state));
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const f of this.listeners) f();
  }

  async command(c: CampaignCommand): Promise<CommandResult> {
    const now = this.now();
    const r = applyCampaignCommand(this.state, this.me, c, now, this.day());
    if (!r.ok) return r;
    this.outbox.push({ seq: this.seq++, at: Math.max(0, now - this.openedAt), cmd: c });
    if (c.t === 'work') this.workMinutes += c.minutes;
    this.emit();
    // What a hero carries goes out at once (a waiting room rebuilds its fight with it), and so do a vote (the others
    // are waiting on it) and another hero (the server may refuse it while that hero is in a fight: better known now).
    this.scheduleFlush(c.t === 'loadout' || c.t === 'carry' || c.t === 'sagaChoice' || c.t === 'sagaVote' || c.t === 'switchHero' || c.t === 'newHero' ? 300 : 2500);
    return r;
  }

  private scheduleFlush(ms: number): void {
    window.clearTimeout(this.flushTimer);
    this.flushTimer = window.setTimeout(() => void this.flush(), ms);
  }

  /** Sends the pending log (and any played battle). */
  async flush(final = false): Promise<void> {
    if (this.inflight) await this.inflight;
    const entries = this.outbox.slice();
    const battles = this.battles.slice();
    const incursions = this.incursionsOut.slice();
    const sagas = this.sagasOut.slice();
    if (this.outbox.length === 0 && battles.length === 0 && incursions.length === 0 && sagas.length === 0 && !final) return;
    const body = {
      entries,
      battles: battles.map((b) => ({ marea: b.marea, wave: b.wave, sector: b.sector, at: Math.max(0, this.now() - this.openedAt), log: b.log })),
      incursions,
      ...(sagas.length ? { sagas } : {}),
      final,
    };
    this.inflight = (async () => {
      try {
        const reply = await api<CommitReply>(`/api/campaigns/${this.id}/sessions/${this.sid}/commit`, { body });
        this.setOffline(false);
        this.outbox = this.outbox.slice(entries.length);
        this.battles = this.battles.slice(battles.length);
        this.incursionsOut = this.incursionsOut.slice(incursions.length);
        this.sagasOut = this.sagasOut.slice(sagas.length);
        if (reply.status === 'rejected') {
          // Our view was wrong: start over from the server's state.
          await this.openSession();
          this.onRebase(entries.length, reply.reason?.startsWith('invalid:') ? [reply.reason.split(':')[2] as string] : undefined);
        } else if (reply.state) {
          this.state = reply.state as CampaignState;
          // Re-apply anything queued after this commit went out.
          for (const e of this.outbox) applyCampaignCommand(this.state, this.me, e.cmd, this.now(), this.day());
          if (reply.discarded.length > 0) this.onRebase(reply.discarded.length, reply.why);
          this.emit();
        }
      } catch (e) {
        if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) {
          // The session is gone (expired or closed): reopen and drop the log the server refused.
          this.outbox = [];
          await this.openSession();
        } else {
          this.setOffline(true);
          this.scheduleFlush(10_000);
        }
      } finally {
        this.inflight = null;
      }
    })();
    await this.inflight;
  }

  private setOffline(v: boolean): void {
    if (this.offline !== v) {
      this.offline = v;
      this.onNetwork(v);
    }
  }

  async prepareBattle(sector: number): Promise<BattleState> {
    await this.flush();
    const b = await api<IssuedBattle>(`/api/campaigns/${this.id}/sessions/${this.sid}/battle`, { body: { sector } });
    return createBattle(b.params);
  }

  async abandonBattle(): Promise<void> {
    await api(`/api/campaigns/${this.id}/release`, { body: {} }).catch(() => undefined);
  }

  private notePlayed(by: Record<string, string> | undefined): void {
    for (const [k, pid] of Object.entries(by ?? {})) if (pid === this.me) this.played.add(k);
  }

  playedNext(): boolean {
    const k = `${this.state.mareaIndex}:${this.state.waveIndex}:`;
    return [...this.played].some((x) => x.startsWith(k));
  }

  attend(): void {
    /* The server resolves waves on its own schedule. */
  }

  async release(): Promise<void> {
    /* Nothing to release: the server resolves the wave when it is due. */
  }

  async submitBattle(p: PlayedBattle): Promise<void> {
    if (p.battle.result !== 'running') this.played.add(`${p.marea}:${p.wave}:${p.sector}`);
    this.battles.push(p);
    await this.flush();
    await this.refresh();
  }

  canPlayNext(): 'live' | 'preview' | false {
    // With the keep fallen the rest of the Marea is not fought.
    if (this.state.status !== 'active' || this.state.mareaLost || this.playedNext()) return false;
    const at = waveAt(this.cal, calendarMarea(this.state), this.state.waveIndex);
    const now = this.now();
    if (now >= at) return 'live';
    const window = previewWindow(this.cal);
    return at - now <= window ? 'preview' : false;
  }

  /** Pull the latest state when nothing of ours is pending. */
  async refresh(): Promise<boolean> {
    if (this.outbox.length > 0 || this.inflight) return false;
    try {
      const v = await api<StateView>(`/api/campaigns/${this.id}/state`);
      this.setOffline(false);
      this.notePlayed(v.playedBy);
      // The server's state is the truth whenever nothing of ours is on its way: take it every time, not
      // only when the version number moved (ours also counts our own changes, so the numbers can match
      // while the contents don't, and a friend's help only showed after a reload).
      this.state = v.state;
      // Anything queued while the answer was on its way is ours too: put it back on top.
      for (const e of this.outbox) applyCampaignCommand(this.state, this.me, e.cmd, this.now(), this.day());
      advanceTime(this.state, this.now());
      this.emit();
      return true;
    } catch {
      this.setOffline(true);
    }
    return false;
  }

  async poll(): Promise<boolean> {
    // Advance golems locally every second; ask the server every 20 s, or right after a wave is due.
    const before = this.state.clock;
    advanceTime(this.state, this.now());
    let changed = this.state.clock !== before;
    const due = this.now() >= waveAt(this.cal, calendarMarea(this.state), this.state.waveIndex);
    const t = Date.now();
    if (t - this.pollTimer > (due ? 4000 : 20_000)) {
      this.pollTimer = t;
      if (await this.refresh()) changed = true;
    }
    if (changed) this.emit();
    return changed;
  }

  /** Stops the beats of time played (see startPlayBeat). */
  private stopBeat: () => void = () => undefined;

  async close(): Promise<void> {
    this.stopBeat();
    window.removeEventListener('pagehide', this.onHide);
    window.clearTimeout(this.flushTimer);
    await this.flush(true).catch(() => undefined);
  }

  day(): number {
    return campaignDay(this.cal, this.now());
  }

  async prepareIncursion(node: number, companions: string[], risk: IncursionRisk, tier: 1 | 2 | 3 = 1): Promise<{ id: string; battle: BattleState }> {
    await this.flush();
    const inc = await api<{ id: string; params: BattleParams }>(`/api/campaigns/${this.id}/sessions/${this.sid}/incursion`, { body: { node, companions, risk, ...(tier > 1 ? { tier } : {}) } });
    return { id: inc.id, battle: createBattle(inc.params) };
  }

  private seen: Presence | null = null;
  /** Work minutes handed in during this session (the server allows one minute ahead of its clock). */
  private workMinutes = 0;

  workAllowance(): number {
    return Math.floor((this.now() - this.openedAt) / 60_000) + 2 - this.workMinutes;
  }

  presence(): Presence | null {
    return this.seen;
  }

  /** Asks who is around and which parties are gathering; cheap, polled every few seconds by the bastion. */
  async pollPresence(pos?: { x: number; y: number }): Promise<Presence | null> {
    try {
      this.seen = await api<Presence>(`/api/campaigns/${this.id}/presence`, pos ? { body: { x: Math.round(pos.x), y: Math.round(pos.y) } } : {});
    } catch {
      /* keep the last one */
    }
    return this.seen;
  }

  /** A line in the keep's chat; the chat as the server has it now (null if it didn't go). */
  async keepSay(text: string): Promise<ChatLine[] | null> {
    try {
      const r = await api<{ chat: ChatLine[] }>(`/api/campaigns/${this.id}/chat`, { body: { text } });
      if (this.seen) this.seen = { ...this.seen, chat: r.chat };
      return r.chat;
    } catch {
      return null;
    }
  }

  prepareDungeon(): BattleState {
    // Online the crypt is a live room (main.ts connects to it); there is no solo run.
    throw new Error('live');
  }

  async submitDungeon(): Promise<void> {
    await this.refresh();
  }

  prepareSima(): BattleState {
    // Online the Chasm is a live room (main.ts connects to it).
    throw new Error('live');
  }

  async submitSima(): Promise<void> {
    await this.refresh();
  }

  async submitIncursion(id: string, _battle: BattleState, log: { tick: number; cmds: Command[] }[]): Promise<void> {
    this.incursionsOut.push({ id, log });
    await this.flush();
    await this.refresh();
  }

  async prepareSaga(rerun = false, bet?: BetId, side?: SideId, ghosts: string[] = [], replay?: number): Promise<{ id: string; battle: BattleState }> {
    await this.flush();
    const g = ghosts.length ? { ghosts } : {};
    const sg = await api<{ id: string; params: BattleParams }>(`/api/campaigns/${this.id}/sessions/${this.sid}/saga`, { body: side ? { side, ...(replay !== undefined ? { replay } : {}) } : bet ? { bet } : rerun ? { rerun, ...g } : g });
    return { id: sg.id, battle: createBattle(sg.params) };
  }

  async submitSaga(id: string, _battle: BattleState, log: { tick: number; cmds: Command[] }[]): Promise<void> {
    this.sagasOut.push({ id, log });
    await this.flush();
    await this.refresh();
  }

  async invite(): Promise<{ code: string; url: string }> {
    return api(`/api/campaigns/${this.id}/invites`, { body: {} });
  }
}
